import { apiClient } from './apiClient';

export type SalesforceAccountServices = {
  finance: string;
  humanCapital: string;
  operations: string;
  otherGA: string;
  privateEquityOperations: string;
  salesMarketing: string;
  strategy: string;
  technology: string;
  turnaroundRestructuring: string;
};

export type SalesforceAccountResponse = {
  id: string;
  name: string;
  introCallCertified?: boolean;
  peClients: string;
  website: string;
  location: string;
  tagline?: string;
  billingAddress?: { city?: string; state?: string };
  peReview: string;
  industries: string;
  services: SalesforceAccountServices;
};

export type SalesforceConciergeResponse = {
  id: string;
  projectDescription: string;
  clientProjectDescription?: string | null;
  portcoInitiativeDescription: string;
  industry: string;
  service: string;
  companyName: string;
};

export type SalesforceCriteriaScorecardResponse = {
  functional_capability: string;
  industry_experience: string;
  price: string;
  geography: string;
};

export type SalesforceKeyCriteriaScorecardResponse = {
  functional_capability: string;
  industry_experience: string;
  budget: string;
  location: string;
};

export type SalesforceQualificationIntroResponse = {
  status: string;
  description: string;
  sequence: number;
};

export type SalesforceCriteriaItem = {
  id: string;
  type: string;
  definition: string;
  top: number;
};

export type SalesforceAvailabilityField = {
  value?: string;
  sub?: string;
};

export type SalesforceProviderAvailabilityResponse = {
  start?: SalesforceAvailabilityField;
  onsite?: SalesforceAvailabilityField;
  isPlaceholder?: boolean;
};

export type SalesforceProviderFeesResponse = {
  value?: string;
  unit?: string;
  note?: string;
  isPlaceholder?: boolean;
};

export type SalesforceProviderOnePagerMetadataResponse = {
  account: SalesforceAccountResponse;
  companyName: string;
  concierge: SalesforceConciergeResponse;
  criteriaScorecard: SalesforceCriteriaScorecardResponse;
  keyCriteriaScorecard: SalesforceKeyCriteriaScorecardResponse;
  qualificationIntros: SalesforceQualificationIntroResponse[];
  criteria?: SalesforceCriteriaItem[];
  // Placeholder until Salesforce captures real provider availability/rate data (BPP-82).
  availability?: SalesforceProviderAvailabilityResponse;
  fees?: SalesforceProviderFeesResponse;
};

const API_V1 = '/api/v1';

export const salesforceService = {
  updateConciergeProjectDescription(introductionId: string, description: string, args?: { signal?: AbortSignal }) {
    return apiClient
      .request(`${API_V1}/salesforce/introduction/${introductionId}/project-description`, {
        method: 'PATCH',
        body: { description },
        signal: args?.signal,
      })
      .then(() => {});
  },

  generateClientProjectDescription(conciergeId: string, args?: { signal?: AbortSignal }) {
    return apiClient
      .request(`${API_V1}/salesforce/concierge/${conciergeId}/client-project-description`, {
        method: 'POST',
        signal: args?.signal,
      })
      .then((res) => res.json() as Promise<{ description: string }>);
  },

  getProviderOnePagerMetadata(accountId: string, introductionId: string, args?: { token?: string; signal?: AbortSignal }) {
    const qs = new URLSearchParams({ introductionId });
    return apiClient.getJson<SalesforceProviderOnePagerMetadataResponse>(
      `${API_V1}/salesforce/providers/${accountId}/one-pager-metadata?${qs.toString()}`,
      {
        headers: {
          'x-user-token': args?.token,
        },
        signal: args?.signal,
      }
    );
  },
};
